/**
 * Evidence chip popover (plan 2 §11.2.1).
 * Fields come from message metadata.evidence; admins may fetch SQL.
 * DOM text is set via text nodes only.
 */
import { el } from '../core/dom.js';
import { api } from '../core/api.js';

/**
 * @param {unknown} value
 * @returns {string}
 */
function formatValue(value) {
  if (value == null) return '—';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * @param {object} evidence
 * @returns {HTMLElement}
 */
function fieldList(evidence) {
  const rows = [
    ['Claim', evidence.claim == null ? '—' : String(evidence.claim)],
    ['Value', formatValue(evidence.value)],
    ['Status', evidence.status == null ? '—' : String(evidence.status)],
    ['Dataset', evidence.dataset == null ? '—' : String(evidence.dataset)],
    ['Rows', evidence.row_count == null ? '—' : String(evidence.row_count)],
  ];
  return el('dl', { class: 'ev-popover-fields' },
    ...rows.flatMap(([label, value]) => [
      el('dt', {}, label),
      el('dd', {}, value),
    ]));
}

/**
 * @param {{
 *   evidence?: Record<string, object>,
 *   runId?: string,
 *   isAdmin?: boolean,
 *   apiGet?: Function,
 * }} [opts]
 */
export function bindEvidenceChips(root, opts) {
  if (!root || typeof root.querySelectorAll !== 'function') return;
  const evidence = (opts && opts.evidence) || {};
  const runId = opts && opts.runId ? String(opts.runId) : '';
  const isAdmin = !!(opts && opts.isAdmin);
  const get = (opts && typeof opts.apiGet === 'function')
    ? opts.apiGet
    : (path) => api(path);

  let openChip = null;
  let popover = null;
  let onDocKey = null;
  let onDocClick = null;

  function close() {
    const chip = openChip;
    if (popover && popover.parentNode) popover.parentNode.removeChild(popover);
    popover = null;
    openChip = null;
    if (onDocKey) {
      document.removeEventListener('keydown', onDocKey);
      onDocKey = null;
    }
    if (onDocClick) {
      document.removeEventListener('click', onDocClick);
      onDocClick = null;
    }
    if (chip) {
      chip.setAttribute('aria-expanded', 'false');
      if (typeof chip.focus === 'function') chip.focus();
    }
  }

  /**
   * @param {HTMLElement} chip
   * @param {string} ref
   */
  function open(chip, ref) {
    if (openChip === chip) {
      close();
      return;
    }
    close();
    const copy = evidence[ref];
    if (!copy) return;

    popover = el('div', {
      class: 'ev-popover',
      role: 'dialog',
      'aria-label': `Evidence ${ref}`,
    },
    el('p', { class: 'ev-popover-ref' }, ref),
    fieldList(copy));

    if (isAdmin && runId) {
      const sqlBox = el('pre', { class: 'ev-popover-sql', hidden: true });
      const sqlBtn = el('button', {
        type: 'button',
        class: 'ev-popover-sql-btn',
      }, 'Show SQL');
      let loaded = false;
      sqlBtn.addEventListener('click', async (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (loaded) {
          sqlBox.hidden = !sqlBox.hidden;
          return;
        }
        sqlBtn.disabled = true;
        sqlBtn.textContent = 'Loading…';
        const res = await get(`/analysis/${encodeURIComponent(runId)}/findings/${encodeURIComponent(ref)}`);
        loaded = true;
        sqlBtn.disabled = false;
        sqlBtn.textContent = 'Show SQL';
        if (!res || res.error || res.status === 404) {
          sqlBox.hidden = false;
          sqlBox.textContent = 'SQL no longer available';
          return;
        }
        const sql = res.finding && res.finding.sql != null ? String(res.finding.sql) : '';
        sqlBox.hidden = false;
        sqlBox.textContent = sql || 'SQL no longer available';
      });
      popover.append(sqlBtn, sqlBox);
    }

    chip.setAttribute('aria-expanded', 'true');
    const host = chip.parentNode || root;
    host.insertBefore(popover, chip.nextSibling);
    openChip = chip;

    onDocKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      }
    };
    onDocClick = (event) => {
      const target = event.target;
      if (!target) return;
      if (popover && (target === popover || (popover.contains && popover.contains(target)))) return;
      if (target === chip || (chip.contains && chip.contains(target))) return;
      close();
    };
    document.addEventListener('keydown', onDocKey);
    // Next tick so the opening click does not immediately close.
    setTimeout(() => {
      if (onDocClick) document.addEventListener('click', onDocClick);
    }, 0);
  }

  const chips = root.querySelectorAll('.ev-chip');
  for (const chip of chips) {
    const ref = chip.getAttribute('data-ev-ref') || '';
    if (!ref || !evidence[ref]) continue;
    chip.addEventListener('click', (event) => {
      event.preventDefault();
      open(chip, ref);
    });
    chip.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        open(chip, ref);
      }
    });
  }

  return { close };
}

/**
 * Build a chip button HTML snippet (label parts escaped by caller).
 * Unknown refs stay as the literal token.
 * @param {string} ref
 * @param {object|null|undefined} copy
 * @param {(s: string) => string} escape
 * @returns {string}
 */
export function evidenceChipHtml(ref, copy, escape) {
  if (!copy || typeof copy !== 'object') return `[[ev:${ref}]]`;
  const dataset = copy.dataset != null && String(copy.dataset).trim()
    ? String(copy.dataset)
    : ref;
  const status = copy.status != null && String(copy.status).trim()
    ? String(copy.status)
    : '';
  const label = status ? `${dataset} · ${status}` : dataset;
  return `<button type="button" class="ev-chip" data-ev-ref="${escape(ref)}" aria-expanded="false" aria-haspopup="dialog">${escape(label)}</button>`;
}
